import { useEffect, useState } from "react";
import { UserService } from "services/UserService";
import { toast } from "sonner";
import { UserRole, User as UserType } from "../../../types/user";

interface AddressData {
  regions: any[];
  districts: any[];
  counties: any[];
  subCounties: any[];
  parishes: any[];
}

interface InstitutionData {
  institutions: any[];
  branches: any[];
  serviceCategories: any[];
}

export const useUserFormData = (editingUser?: UserType | null) => {
  const [formData, setFormData] = useState<any>({
    // Basic Info
    name: editingUser?.name || "",
    firstName: editingUser?.firstName || "",
    lastName: editingUser?.lastName || "",
    email: editingUser?.email || "",
    phone: editingUser?.phone || "",
    avatar: editingUser?.avatar || "",
    gender: editingUser?.gender || "",

    // Address
    region: "",
    district: "",
    county: "",
    subCounty: "",
    parish: "",
    streetAddress: "",

    // Institution & Branch (for applicable roles)
    institutionId: "",
    branchId: "",
    serviceCategoryId: "",
  });

  const [addressData, setAddressData] = useState<AddressData>({
    regions: [],
    districts: [],
    counties: [],
    subCounties: [],
    parishes: [],
  });

  const [institutionData, setInstitutionData] = useState<InstitutionData>({
    institutions: [],
    branches: [],
    serviceCategories: [],
  });

  const {
    fetchRegions,
    fetchDistricts,
    fetchCounties,
    fetchSubCounties,
    fetchParishes,
    fetchInstitutions,
    fetchBranches,
    fetchServiceCategories,
    createInstitution,
    createBranch,
    createServiceCategory,
    createUser,
    updateUser,
  } = UserService();


  const loadRegions = async () => {
    const regions = await fetchRegions();
    setAddressData((prev) => ({ ...prev, regions }));
  };

  const loadInstitutions = async () => {
    const institutions = await fetchInstitutions();
    setInstitutionData((prev) => ({ ...prev, institutions }));
  };

  const loadServiceCategories = async () => {
    const serviceCategories = await fetchServiceCategories();
    setInstitutionData((prev) => ({ ...prev, serviceCategories }));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadRegions(),
        loadInstitutions(),
        loadServiceCategories(),
      ]);
    };
    loadInitialData();
  }, []);


  const handleAddressChange = async (field: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));

    if (field === "region") {
      const districts = await fetchDistricts(parseInt(value));
      setAddressData((prev) => ({
        ...prev,
        districts,
        counties: [],
        subCounties: [],
        parishes: [],
      }));
      setFormData((prev: any) => ({
        ...prev,
        district: "",
        county: "",
        subCounty: "",
        parish: "",
      }));
    } else if (field === "district") {
      const counties = await fetchCounties(parseInt(value));
      setAddressData((prev: any) => ({
        ...prev,
        counties,
        subCounties: [],
        parishes: [],
      }));
      setFormData((prev: any) => ({
        ...prev,
        county: "",
        subCounty: "",
        parish: "",
      }));
    } else if (field === "county") {
      const subCounties = await fetchSubCounties(parseInt(value));
      setAddressData((prev) => ({
        ...prev,
        subCounties,
        parishes: [],
      }));
      setFormData((prev: any) => ({
        ...prev,
        subCounty: "",
        parish: "",
      }));
    } else if (field === "subCounty") {
      const parishes = await fetchParishes(parseInt(value));
      setAddressData((prev) => ({ ...prev, parishes }));
      setFormData((prev: any) => ({ ...prev, parish: "" }));
    }
  };

  const handleInstitutionChange = async (institutionId: string) => {
    setFormData((prev: any) => ({ ...prev, institutionId, branchId: "" }));
    if (institutionId) {
      const branches = await fetchBranches(institutionId);
      setInstitutionData((prev) => ({ ...prev, branches }));
    } else {
      setInstitutionData((prev) => ({ ...prev, branches: [] }));
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleCreateInstitution = async (institutionFormData: FormData) => {
    const institutionData = {
      name: institutionFormData.get("name"),
      type: institutionFormData.get("type"),
      description: institutionFormData.get("description"),
      address: institutionFormData.get("address"),
      phone: institutionFormData.get("phone"),
      email: institutionFormData.get("email"),
    };

    const result = await createInstitution(institutionData);
    if (result.success) {
      toast.success("Institution created successfully");
      await loadInstitutions();
      setFormData((prev: any) => ({ ...prev, institutionId: result.data.id }));
      return true;
    } else {
      toast.error("Failed to create institution");
      return false;
    }
  };

  const handleCreateBranch = async (branchFormData: FormData) => {
    const branchData = {
      institutionId: formData.institutionId,
      name: branchFormData.get("name"),
      address: branchFormData.get("address"),
      phone: branchFormData.get("phone"),
      managerName: branchFormData.get("managerName"),
    };

    const result = await createBranch(branchData);
    if (result.success) {
      toast.success("Branch created successfully");
      const branches = await fetchBranches(formData.institutionId);
      setInstitutionData((prev) => ({ ...prev, branches }));
      setFormData((prev: any) => ({ ...prev, branchId: result.data.id }));
      return true;
    } else {
      toast.error("Failed to create branch");
      return false;
    }
  };

  const handleCreateServiceCategory = async (categoryFormData: FormData) => {
    const categoryData = {
      name: categoryFormData.get("name"),
      description: categoryFormData.get("description"),
      parentCategoryId: categoryFormData.get("parentCategory") || null,
    };

    const result = await createServiceCategory(categoryData);
    if (result.success) {
      toast.success("Service category created successfully");
      await loadServiceCategories();
      setFormData((prev: any) => ({
        ...prev,
        serviceCategoryId: result.data.id,
      }));
      return true;
    } else {
      toast.error("Failed to create service category");
      return false;
    }
  };

  const handleSubmit = async (selectedRole: UserRole) => {
    try {
      const userData = {
        ...formData,
        role: selectedRole,
        status: "active",
      };

      let result: any;
      if (editingUser) {
        result = await updateUser(editingUser.id, userData);
      } else {
        result = await createUser(userData);
      }

      if (result.success) {
        toast.success(
          editingUser
            ? "User updated successfully"
            : "User created successfully"
        );
        return { success: true, data: userData };
      } else {
        toast.error(result.error.message);
        return { success: false };
      }
    } catch (error) {
      toast.error("An error occurred while saving user");
      return { success: false };
    }
  };

  return {
    formData,
    addressData,
    institutionData,
    handleAddressChange,
    handleInstitutionChange,
    handleInputChange,
    handleCreateInstitution,
    handleCreateBranch,
    handleCreateServiceCategory,
    handleSubmit,
  };
};
